import { RootStore } from './root';
import { WebAuthnChallengeData } from '@wix/ambassador-iam-authentication-v1-authentication/types';
import {
  DOM_EXCEPTION_NAMES,
  ERROR_CODES,
  extractErrorKeyByErrorCode,
} from '../utils/errorHandler';
import { PublicKeyCredentialWithJSON } from '../types';
import { serializeAuthenticationCredential } from '@wix/webauthn-management';
import { passkeyConnectionsSucceedSrc5Evid5022 } from '@wix/bi-logger-identity-data-account-settings/v2';
import { logInErrorModalViewSrc6Evid11 } from '@wix/bi-logger-identity-data/v2';

/**
 * Result type for WebAuthn operations that distinguishes between
 * success, user cancellation, and actual errors.
 */
export type WebAuthnResult<T> =
  | { status: 'success'; data: T }
  | { status: 'cancelled' }
  | { status: 'error'; errorCode: string; errorMessage?: string };

class WebAuthnError extends Error {
  readonly code: string;
  constructor(code: string, message?: string, options?: ErrorOptions) {
    super(message ?? code, options);
    this.name = 'WebAuthnError';
    this.code = code;
  }
}

export class WebAuthnStore {
  private readonly rootStore: RootStore;
  private webAuthnAbortController: AbortController | null = null;
  private _passkeyFlowDisabled: boolean = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  get isPasskeyFlowDisabled(): boolean {
    return this._passkeyFlowDisabled;
  }

  public disablePasskeyFlow(): void {
    this._passkeyFlowDisabled = true;
  }

  /**
   * Classify a WebAuthn error into cancelled, error with code, or unknown error.
   * Returns the appropriate WebAuthnResult status.
   */
  private classifyWebAuthnError(error: unknown): WebAuthnResult<never> {
    if (!(error instanceof Error)) {
      return {
        status: 'error',
        errorCode: ERROR_CODES.TECHNICAL_ERROR,
        errorMessage: String(error),
      };
    }

    // NotAllowedError covers multiple scenarios: user cancelled, verification failed
    // (wrong biometric), timeout, or no matching credentials. Since we can't
    // distinguish between them, show a generic passkey error.
    if (error.name === DOM_EXCEPTION_NAMES.NOT_ALLOWED_ERROR) {
      return {
        status: 'error',
        errorCode: ERROR_CODES.WEBAUTHN_NOT_ALLOWED,
        errorMessage: error.message,
      };
    }

    // Intentional abort (e.g., switching from usernameless to regular flow)
    // This is the only true "cancelled" case - programmatic abort by our code.
    if (error.name === DOM_EXCEPTION_NAMES.ABORT_ERROR) {
      return { status: 'cancelled' };
    }

    // OperationError with "A request is already pending" means another
    // credentials.get() is in progress (race between usernameless conditional
    // mediation and email-specific flow). Treat as cancelled.
    if (error.name === DOM_EXCEPTION_NAMES.OPERATION_ERROR) {
      return { status: 'cancelled' };
    }

    if (error instanceof WebAuthnError) {
      if (error.code === ERROR_CODES.WEBAUTHN_FAILED_OR_TIMEOUT) {
        this.disablePasskeyFlow();
      }
      return {
        status: 'error',
        errorCode: error.code,
        errorMessage: error.message,
      };
    }

    console.error('[WebAuthn] Unclassified error:', error);
    return {
      status: 'error',
      errorCode: ERROR_CODES.TECHNICAL_ERROR,
      errorMessage: error.message,
    };
  }

  /**
   * Show error toast for WebAuthn errors (not for cancellation).
   */
  private showWebAuthnErrorToast(errorCode: string): void {
    const errorKey = extractErrorKeyByErrorCode(errorCode);
    if (errorKey) {
      this.rootStore.notificationStore.openErrorToast(
        this.rootStore.i18n.t(errorKey),
      );
    }
  }

  private async validateWebAuthnSupport(): Promise<void> {
    try {
      const isSupported =
        window.PublicKeyCredential &&
        (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable());

      if (!isSupported) {
        throw new WebAuthnError(
          ERROR_CODES.WEBAUTHN_NOT_SUPPORTED,
          'Platform authenticator not available',
        );
      }
    } catch (error) {
      throw new WebAuthnError(
        ERROR_CODES.WEBAUTHN_NOT_SUPPORTED,
        'WebAuthn support check failed',
        { cause: error },
      );
    }
  }

  private async createWebAuthnChallenge(
    email?: string,
  ): Promise<WebAuthnChallengeData> {
    let challengeResponse;
    try {
      challengeResponse = await this.rootStore.apiStore.getWebauthnChallenge({
        loginId: { email },
      });
    } catch (error) {
      console.error('[WebAuthn] Challenge API failed:', error);
      throw new WebAuthnError(
        ERROR_CODES.WEBAUTHN_CHALLENGE_FAILED,
        'Challenge API request failed',
        { cause: error },
      );
    }

    if (!challengeResponse.webauthn) {
      throw new WebAuthnError(
        ERROR_CODES.WEBAUTHN_CHALLENGE_FAILED,
        'Challenge response missing webauthn data',
      );
    }
    return challengeResponse.webauthn;
  }

  private parseCredentialRequestOptions(
    challengeData: WebAuthnChallengeData,
  ): PublicKeyCredentialRequestOptions {
    if (!challengeData.publicKeyCredentialRequestOptionsJson) {
      throw new WebAuthnError(
        ERROR_CODES.WEBAUTHN_CHALLENGE_FAILED,
        'Missing publicKeyCredentialRequestOptionsJson',
      );
    }

    try {
      const { publicKey } = JSON.parse(
        challengeData.publicKeyCredentialRequestOptionsJson,
      );

      return (
        PublicKeyCredential as unknown as PublicKeyCredentialWithJSON
      ).parseRequestOptionsFromJSON(publicKey);
    } catch (error) {
      throw new WebAuthnError(
        ERROR_CODES.WEBAUTHN_CHALLENGE_FAILED,
        'Failed to parse credential request options',
        { cause: error },
      );
    }
  }

  private async requestCredentialFromBrowser(
    credentialRequestOptions: PublicKeyCredentialRequestOptions,
    useConditionalUI?: boolean,
    signal?: AbortSignal,
  ): Promise<PublicKeyCredential> {
    const credential = await navigator.credentials.get({
      publicKey: credentialRequestOptions,
      ...(useConditionalUI && {
        mediation: 'conditional' as CredentialMediationRequirement,
      }),
      ...(signal && { signal }),
    });

    if (!credential) {
      throw new WebAuthnError(
        ERROR_CODES.WEBAUTHN_FAILED_OR_TIMEOUT,
        'Browser returned no credential',
      );
    }

    return credential as PublicKeyCredential;
  }

  private async isConditionalMediationAvailable(): Promise<boolean> {
    return PublicKeyCredential.isConditionalMediationAvailable();
  }

  private reportPasskeySuccess(): void {
    this.rootStore.biLogger.report(
      passkeyConnectionsSucceedSrc5Evid5022({
        origin: 'login',
        ldSessionID: this.rootStore.sessionId.get(),
      }),
    );
  }

  public reportPasskeyError(errorCode: string, errorMessage?: string): void {
    this.rootStore.biLogger.report(
      logInErrorModalViewSrc6Evid11({
        origin: 'login',
        error_name: errorCode,
        method: 'passkey',
        error_message: errorMessage,
      }),
    );
  }

  public async authenticateWithWebAuthn(email: string): Promise<
    WebAuthnResult<{
      credential: PublicKeyCredential;
      challenge: WebAuthnChallengeData;
    }>
  > {
    try {
      await this.validateWebAuthnSupport();

      const challenge = await this.createWebAuthnChallenge(email);

      const credentialRequestOptions =
        this.parseCredentialRequestOptions(challenge);
      const credential = await this.requestCredentialFromBrowser(
        credentialRequestOptions,
      );

      this.reportPasskeySuccess();
      return { status: 'success', data: { credential, challenge } };
    } catch (error) {
      return this.classifyWebAuthnError(error);
    }
  }

  public async authenticateWithUsernamelessWebAuthn(): Promise<
    WebAuthnResult<{
      credential: PublicKeyCredential;
      challenge: WebAuthnChallengeData;
    }>
  > {
    try {
      this.webAuthnAbortController = new AbortController();
      const { signal } = this.webAuthnAbortController;

      await this.validateWebAuthnSupport();

      const isConditionalMediationAvailable =
        await this.isConditionalMediationAvailable();
      if (!isConditionalMediationAvailable) {
        // Conditional UI not supported - this is a capability issue, not cancellation
        return {
          status: 'error',
          errorCode: ERROR_CODES.WEBAUTHN_NOT_SUPPORTED,
        };
      }

      const challenge = await this.createWebAuthnChallenge();

      if (signal.aborted) {
        return { status: 'cancelled' };
      }

      const credentialRequestOptions =
        this.parseCredentialRequestOptions(challenge);

      const credential = await this.requestCredentialFromBrowser(
        credentialRequestOptions,
        true,
        signal,
      );

      this.reportPasskeySuccess();
      return { status: 'success', data: { credential, challenge } };
    } catch (error) {
      return this.classifyWebAuthnError(error);
    }
  }

  public abortWebAuthnAuthentication(): void {
    if (this.webAuthnAbortController) {
      this.webAuthnAbortController.abort();
      this.webAuthnAbortController = null;
    }
  }

  /**
   * Complete passkey login flow: authenticate with browser and login.
   * Returns the result status so callers can handle errors appropriately.
   */
  public async loginWithPasskey(email: string): Promise<WebAuthnResult<void>> {
    const result = await this.authenticateWithWebAuthn(email);

    if (result.status !== 'success') {
      // Show error toast for actual errors (not cancellation or silent fallback errors)
      // WEBAUTHN_NOT_ALLOWED covers user cancel, timeout, and biometric verification failure -
      // we silently fall back to password login for these cases without showing an error
      const silentErrors = [
        ERROR_CODES.WEBAUTHN_CHALLENGE_FAILED,
        ERROR_CODES.WEBAUTHN_NOT_SUPPORTED,
        ERROR_CODES.WEBAUTHN_NOT_ALLOWED,
      ];
      if (
        result.status === 'error' &&
        !silentErrors.includes(result.errorCode)
      ) {
        this.reportPasskeyError(result.errorCode, result.errorMessage);
        this.showWebAuthnErrorToast(result.errorCode);
      }
      return result;
    }

    try {
      await this.rootStore.loginStore.login(email, {
        publicKeyCredential: result.data.credential,
        webAuthnChallenge: result.data.challenge,
      });
      return { status: 'success', data: undefined };
    } catch (error: unknown) {
      const errorCode =
        (error as { errorCode?: string })?.errorCode ||
        ERROR_CODES.TECHNICAL_ERROR;
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      return { status: 'error', errorCode, errorMessage };
    }
  }

  /**
   * Authenticate using a 2FA challenge (from switchToMethod response).
   * Returns the credential to be sent to verifyCodeAndLogin.
   */
  public async authenticateWith2FAChallenge(challengeData: {
    challengeId?: string;
    publicKeyCredentialRequestOptionsJson?: string;
  }): Promise<WebAuthnResult<{ challengeId: string; credential: string }>> {
    if (
      !challengeData.challengeId ||
      !challengeData.publicKeyCredentialRequestOptionsJson
    ) {
      return {
        status: 'error',
        errorCode: ERROR_CODES.WEBAUTHN_CHALLENGE_FAILED,
      };
    }

    try {
      await this.validateWebAuthnSupport();

      const credentialRequestOptions = this.parseCredentialRequestOptions({
        publicKeyCredentialRequestOptionsJson:
          challengeData.publicKeyCredentialRequestOptionsJson,
      } as WebAuthnChallengeData);

      const credential = await this.requestCredentialFromBrowser(
        credentialRequestOptions,
      );

      return {
        status: 'success',
        data: {
          challengeId: challengeData.challengeId,
          credential: serializeAuthenticationCredential(
            credential as PublicKeyCredential,
          ),
        },
      };
    } catch (error) {
      return this.classifyWebAuthnError(error);
    }
  }
}
